import { Icon } from "@chakra-ui/react";
import { MdBarChart } from "react-icons/md";
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { translate } from "../../../i18n";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { getDemoCourtsUsageRatio, isDemoOrganization } from "../demoAnalyticsData";
import { AnalyticsFilterParams } from "../hooks/analyticsFilterParams";
import { useGetAllCourtsUsage } from "../hooks/useGetAllCourtsUsage";
import { computeGrowth } from "../utils/computeGrowth";
import { UNKNOWN_STAT_VALUE, isUnreadable, statPlaceholder } from "../utils/statValue";

type Props = {
    boxColor: string;
    brandColor: string;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date | null;
    previousEndDate: Date | null;
    growthLabel?: string;
    filters?: AnalyticsFilterParams;
};
export default function AllCourtsUsageAsPercentageCard({ boxColor, brandColor, startDate, endDate, previousStartDate, previousEndDate, growthLabel, filters }: Props) {
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const isDemo = isDemoOrganization(selectedOrganization?.id);
    /**
     * `GET analytics/all-courts-usage` carries `@UseGuards(AdminAuthGuard)` —
     * read from `api/src/analytics/analytics.controller.ts` this run — so a
     * stale token or a revoked admin is refused with no outage at all, on top
     * of the ordinary 5xx/network paths.
     */
    const { data, isInitialLoading: isPending, isError } = useGetAllCourtsUsage(selectedLocation?.id, startDate, endDate, filters);
    // Comparison read: feeds `computeGrowth` only. Its undefined now SURVIVES
    // to `computeGrowth` (see the ratio comment below), which is what makes the
    // allowlist entry in `analytics-read-error-gate` true rather than hopeful.
    const { data: previousData } = useGetAllCourtsUsage(
        selectedLocation?.id,
        previousStartDate ?? startDate,
        previousEndDate ?? endDate,
        filters,
    );

    const orgId = selectedOrganization?.id;
    const unreadable = isUnreadable(isError, data);
    const demoRatio = isDemo ? getDemoCourtsUsageRatio(startDate, endDate, orgId) : 0;
    const demoPrevRatio = isDemo && previousStartDate && previousEndDate
        ? getDemoCourtsUsageRatio(previousStartDate, previousEndDate, orgId) : 0;

    const totalGameHours = data?.reduce((acc, curr) => acc + curr.totalGameDurationsInMs, 0);
    const totalAvailabilityHours = data?.reduce((acc, curr) => acc + curr.totalAvailabilityInMs, 0);
    /**
     * `undefined`, not `0`, when the hours are not there.
     *
     * The old `: 0` was this defect one derivative along, and it is the worse
     * half: `computeGrowth` guards against an undefined argument, so a real
     * zero substituted BEFORE the call disarms the guard. A refused period read
     * beside a previous read that succeeded (cached, or simply a second request
     * that did not fail) therefore produced `0%` with a red arrow reading
     * `-100.0% vs previous period` — an alarming, false, completely confident
     * statement about the venue's occupancy.
     */
    const ratio = isDemo ? demoRatio : (totalGameHours != null && totalAvailabilityHours) ? totalGameHours / totalAvailabilityHours : undefined;

    const prevTotalGameHours = previousData?.reduce((acc, curr) => acc + curr.totalGameDurationsInMs, 0);
    const prevTotalAvailabilityHours = previousData?.reduce((acc, curr) => acc + curr.totalAvailabilityInMs, 0);
    const prevRatio = isDemo ? demoPrevRatio : (prevTotalGameHours != null && prevTotalAvailabilityHours) ? prevTotalGameHours / prevTotalAvailabilityHours : undefined;

    const demoGrowthRaw = demoPrevRatio > 0
        ? (demoRatio - demoPrevRatio) / demoPrevRatio * 100
        : demoRatio > 0 ? 100 : 0;
    const growth = isDemo
        ? (previousStartDate ? +Math.max(8, demoGrowthRaw).toFixed(1) : undefined)
        : previousStartDate ? computeGrowth(ratio, prevRatio) : undefined;
    const ratioLabel = `${((ratio ?? 0) * 100).toFixed(1)}%`;
    /**
     * `ratio !== undefined`, not `ratio`, and a placeholder rather than a
     * literal `'0%'` in the last arm.
     *
     * This read is gated on `!!locationId`, and `/analytics` renders the card
     * whether or not a location is selected — an organization with no location,
     * or one whose locations read failed, leaves the query DISABLED, which
     * React Query v4 reports as `isLoading: true` with `fetchStatus: 'idle'`,
     * so this card sat on `--` forever (R100-1). With that last arm no longer a
     * zero, a truthiness test on `ratio` would send a genuinely EMPTY week — a
     * real 0% occupancy, which this card exists to report — to the unknown
     * placeholder instead.
     */
    const value = isDemo ? ratioLabel : unreadable ? UNKNOWN_STAT_VALUE : ratio !== undefined ? ratioLabel : statPlaceholder(isPending);

    return (
        <MiniStatistics
            startContent={
                <IconBox
                    w='56px'
                    h='56px'
                    bg={boxColor}
                    icon={<Icon w='32px' h='32px' as={MdBarChart} color={brandColor} />}
                />
            }
            name={translate('analytics.courts_usage')}
            value={value}
            isError={!isDemo && unreadable}
            growth={growth}
            growthLabel={growthLabel}
        />)
}
